// Shared protocol types for the HomeCloud PWA.
// Mirrors the Go server's hub.Message envelope and the Rust host's file tree.

/** Signaling frames exchanged with the Go server over WebSocket. */
export type SignalingMessage =
  | { type: 'welcome'; from: string; userId: string; connId: string }
  | { type: 'presence'; from: string; total: number; roles: string[]; pcId?: string }
  | {
      type: 'offer';
      from?: string;
      target?: string;
      pcId?: string;
      sdp?: RTCSessionDescriptionInit;
    }
  | { type: 'answer'; from?: string; target?: string; pcId?: string; sdp?: RTCSessionDescriptionInit }
  | {
      type: 'ice-candidate';
      from?: string;
      target?: string;
      pcId?: string;
      candidate?: RTCIceCandidateInit;
    }
  | { type: 'peer-unavailable'; from?: string; target?: string }
  | { type: 'host-busy'; from?: string; target?: string; pcId?: string; reason?: string }
  | { type: 'p2p-release'; target?: string; pcId?: string }
  | { type: 'hello'; role: 'host' | 'viewer'; pcId?: string }
  | { type: 'watch'; target: string; pcId?: string }
  | { type: 'error'; error?: string };

/** A node of the folder structure returned by the desktop host. */
export interface FileNode {
  /** Path relative to the shared root, "" for the root itself. */
  name: string;
  path: string;
  /** "dir" or "file". */
  kind: 'dir' | 'file';
  /** Child entries — present on directories only. */
  children?: FileNode[];
  /** Size in bytes — files only. */
  size?: number;
  /** Last modified time (ms since epoch) when the host provides it. */
  modifiedMs?: number;
  /** True when a JPEG thumbnail exists on the host (images). */
  hasThumb?: boolean;
}

/** Envelope for LIST_FILES responses traveling over the RTCDataChannel. */
export interface FilesResponse {
  type: 'FILES';
  root: string;
  node: FileNode;
  /** Host-generated timestamp for the UI. */
  generatedAt: string;
}

/** Client -> host data-channel commands (text frames). */
export const LIST_FILES = 'LIST_FILES';

/** When a PC exposes several shares, the PWA names which root to list. */
export interface ListFilesRequest {
  cmd: 'LIST_FILES';
  /** Host-side folder path key (same string as on the desktop host). */
  folder?: string;
  /** Path relative to the share root to list (omit = share root). */
  path?: string;
}

/** Build the control-channel frame that requests a folder listing from the host. */
export function listFilesRequest(shareFolder?: string, relPath?: string): string {
  const folder = shareFolder?.trim();
  const path = relPath?.trim();
  if (!folder && !path) return LIST_FILES;
  return JSON.stringify({
    cmd: 'LIST_FILES',
    folder: folder || undefined,
    path: path || undefined,
  } satisfies ListFilesRequest);
}

/** Create a subdirectory under the current shared folder (JSON on the control channel). */
export interface MkdirRequest {
  cmd: 'MKDIR';
  /** Path relative to the shared root, e.g. `docs/2026`. */
  path: string;
}

export interface DeleteRequest {
  cmd: 'DELETE';
  /** Path relative to the shared root. */
  path: string;
}

export interface MoveRequest {
  cmd: 'MOVE';
  from: string;
  to: string;
}

export interface GetThumbRequest {
  cmd: 'GET_THUMB';
  path: string;
}

/** Host → PWA: feature tier and identity (sent when the control channel opens). */
export interface HostInfoMessage {
  type: 'host-info';
  planTier: 'paid' | 'free';
  pcId?: string;
}

/** Host → PWA: thumbnail bytes (base64 JPEG). */
export interface ThumbMessage {
  type: 'thumb';
  path: string;
  mime?: string;
  data?: string;
  error?: string;
}

export type PlanTier = 'paid' | 'free';

export function getThumbRequest(relPath: string): string {
  return JSON.stringify({ cmd: 'GET_THUMB', path: relPath } satisfies GetThumbRequest);
}

/** Label of the binary transfer channel (kept separate from control). */
export const XFER_LABEL = 'xfer';
/** Chunk size for file streaming over the data channel. */
export const XFER_CHUNK = 64 * 1024;

/** Transfer frames traveling as TEXT on the xfer channel (binary = file bytes). */
export type XferFrame =
  | { op: 'get'; path: string } // PWA -> host: stream this shared-folder path
  | { op: 'put-begin'; name: string; size: number; dir?: string } // PWA -> host: upload starts
  | { op: 'put-cancel' } // PWA -> host: abandon in-flight upload
  | { type: 'file-meta'; name: string; size: number; mime: string } // host -> PWA
  | { type: 'file-end'; name: string; size: number } // host -> PWA: stream complete
  | { type: 'put-done'; name: string; size: number; path: string } // host -> PWA
  | { type: 'xfer-error'; error: string }; // host -> PWA

/** Live progress of the one active transfer, surfaced in the UI. */
export interface XferProgress {
  dir: 'down' | 'up';
  name: string;
  total: number;
  done: number;
}

/** WebSocket endpoint paths relative to the signaling server origin. */
export const API = {
  register: '/api/register',
  registerSendCode: '/api/register/send-code',
  registerConfirmCode: '/api/register/confirm-code',
  login: '/api/login',
  stats: '/api/stats',
  ice: '/api/ice',
  ws: '/ws',
  pcs: '/api/pcs',
  invitesMine: '/api/invites/mine',
  claimInvites: '/api/invites/claim',
  /** Item 2: one-time encryption-password initialisation (409 when set). */
  setEncPassword: '/api/set-enc-password',
  /** Item 2: check a candidate encryption password against the verifier. */
  verifyEncPassword: '/api/verify-enc-password',
} as const;

/** Fallback STUN set when /api/ice is unreachable — identical on host and PWA. */
export const ICE_SERVERS: RTCIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
];
